import { type JSONContent, type MarkdownParseHelpers, type MarkdownRendererHelpers, type MarkdownToken, Node, type RenderContext } from "@tiptap/core";

// A `{: ...}` line under a block is that block's attribute and is written back under it.
export const BlockIal = Node.create({
	name: "blockIal",
	group: "block",
	atom: true,
	addAttributes: () => ({ ial: { default: "" }, after: { default: false } }),
	renderHTML: () => ["div", { "data-block-ial": "" }],
	markdownTokenizer: {
		name: "blockIal",
		level: "block",
		start: (src: string) => src.search(/\n {0,3}\{:/),
		tokenize: (src: string) => {
			const m = /^ {0,3}\{:(\^?)((?:[^}"\n]|"[^"\n]*")*)\}[ \t]*(?:\n|$)/.exec(src);
			return m ? { type: "blockIal", raw: m[0], after: m[1] === "^", ial: `{:${m[2]}}` } : undefined;
		},
	},
	parseMarkdown: (t: MarkdownToken) => ({ type: "blockIal", attrs: { ial: t.ial, after: t.after } }),
	renderMarkdown: (node: JSONContent) => node.attrs?.ial,
});

const FENCE = /^(?:> ?)* {0,3}(`{3,}|~{3,})(.*)$/;

// The fence a line leaves open, as Markdown::Notation tracks it: a line that opens, continues or closes a fence is code, not notation.
export function nextFence(line: string, fence: string | null): string | null {
	if (fence) return new RegExp(`^(?:> ?)* {0,3}${fence[0] === "`" ? "`" : "~"}{${fence.length},}[ \\t]*$`).test(line) ? null : fence;
	const open = FENCE.exec(line);
	return open && !(open[1][0] === "`" && open[2].includes("`")) ? open[1] : null;
}

// A `:::` after quote marks or indentation is text, as in Markdown::Notation. It carries this mark while
// marked reads it, and restoreColons takes the mark off again.
const NOT_A_BOX = String.fromCharCode(0xe003);
const quoteDepth = (line: string) => (/^(?:>[ \t]?)*/.exec(line)?.[0].match(/>/g) ?? []).length;

// Source preparation, outside fenced code. An IAL line right under a block (no blank line between, inside the same
// quotes) is marked `{:^` and split off with a blank line so marked stops reading it as continuation text.
export function prepare(markdown: string): string {
	const lines = markdown.split("\n");
	let fence: string | null = null;
	return lines
		.map((line, i) => {
			const inside = fence;
			fence = nextFence(line, fence);
			if (inside || fence) return line;
			const text = line.replace(/^([ \t]+|[ \t]*(?:>[ \t]?)+[ \t]*)(?=:::)/, `$1${NOT_A_BOX}`);
			const ial = /^((?:>[ \t]?)*)( {0,3}\{:(?:[^}"\n]|"[^"\n]*")*\}[ \t]*)$/.exec(text);
			const previous = lines[i - 1]?.replace(/^(?:>[ \t]?)*/, "").trim();
			return ial && lines[i - 1]?.trim() && (!ial[1] || (previous && quoteDepth(lines[i - 1]) >= quoteDepth(text)))
				? `${ial[1].trimEnd()}\n${ial[1]}${ial[2].replace("{:", "{:^")}`
				: text;
		})
		.join("\n");
}

export function attachIals(nodes: JSONContent[]): JSONContent[] {
	const out: JSONContent[] = [];
	for (const node of nodes) {
		if (node.content) node.content = attachIals(node.content);
		const prev = out[out.length - 1];
		if (node.type === "blockIal" && node.attrs?.after && prev && "ial" in (prev.attrs ?? { ial: null })) {
			prev.attrs = { ...prev.attrs, ial: prev.attrs?.ial ? `${prev.attrs.ial}\n${node.attrs.ial}` : node.attrs.ial };
			continue;
		}
		out.push(node);
	}
	return out;
}

export const withIal = (ext: Node) =>
	ext.extend({
		addAttributes() {
			return { ...this.parent?.(), ial: { default: null, rendered: false } };
		},
		parseMarkdown(token: MarkdownToken, helpers: MarkdownParseHelpers) {
			const result = this.parent?.(token, helpers);
			if (result && !Array.isArray(result)) result.attrs = { ...(result.attrs ?? {}), ial: null };
			return result;
		},
		renderMarkdown(node: JSONContent, helpers: MarkdownRendererHelpers, ctx: RenderContext) {
			const body = this.parent?.(node, helpers, ctx) ?? "";
			return node.attrs?.ial ? `${body.replace(/\n+$/, "")}\n${node.attrs.ial}` : body;
		},
	});

// Undoes the split prepare makes before an IAL line, for text kept as written.
export const unprepare = (raw: string) => raw.replace(/\n\n( {0,3})\{:\^/g, "\n$1{:");

export function restoreColons<T>(value: T): T {
	if (typeof value === "string") return value.replaceAll(NOT_A_BOX, "") as T;
	if (Array.isArray(value)) return value.map(restoreColons) as T;
	if (value && typeof value === "object") return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, restoreColons(item)])) as T;
	return value;
}
